import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { useAuth } from "./AuthProvider";
import { Spinner } from "../components/Spinner";

interface Props {
  children: ReactNode;
}

/** Gates a subtree behind authentication.
 * - while booting → spinner
 * - unauthenticated → redirect to /login (preserving location for return-after-login)
 * - authenticated + must_change_password → force /change-password
 * - authenticated otherwise → render children
 */
export function RequireAuth({ children }: Props) {
  const { status, me } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center paper-grain">
        <Spinner label="Loading edition" />
      </div>
    );
  }

  if (status === "unauthenticated" || !me) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    me.must_change_password &&
    location.pathname !== "/change-password"
  ) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}
